/**
 * Atlas PM · 项目星图 · 右侧「任务列表」面板（对齐原型 #startl）
 *
 * 原型：#startl `top:136px;right:20px;bottom:22px;width:290px`，标题「任务列表」，
 * 副标题「N 个 · 按形态聚类」，每行：语义色圆点 + 任务名 + 形态标签 + 负责人 + 进度。
 *
 * ⚠️ P5.1 决策：原左栏的「任务状态统计」属于原型没有、但信息有用的内容，
 *    按任务书建议 (a) 迁移到本面板顶部，作为紧凑状态条；原型任务列表的
 *    标题与行文案保持不变。
 */

import type { StarLayoutNode } from './layout';

export interface StarStatusCounts {
  inProgress: number;
  done: number;
  approving: number;
  overdue: number;
  notStarted: number;
}

export interface StarTaskListProps {
  nodes: readonly StarLayoutNode[];
  selectedId: string | null;
  counts: StarStatusCounts;
  onSelect: (id: string) => void;
  filterActive?: boolean;
  onClearFilter?: () => void;
}

const STATUS_STRIP: Array<{ key: keyof StarStatusCounts; label: string; color: string }> = [
  { key: 'inProgress', label: '进行中', color: 'var(--run)' },
  { key: 'done', label: '已冻结', color: 'var(--frozen)' },
  { key: 'approving', label: '审批中', color: 'var(--warn)' },
  { key: 'overdue', label: '逾期', color: 'var(--alert)' },
  { key: 'notStarted', label: '草稿', color: 'var(--idle)' },
];

export default function StarTaskList({
  nodes,
  selectedId,
  counts,
  onSelect,
  filterActive = false,
  onClearFilter,
}: StarTaskListProps) {
  return (
    <aside className="star-tasklist" aria-label="任务列表">
      <div className="star-tasklist__head">
        <span>任务列表</span>
        <span className="cnt">{nodes.length} 个 · 按形态聚类</span>
      </div>

      <div className="star-tasklist__status" aria-label="任务状态统计">
        {STATUS_STRIP.map((s) => (
          <span className="star-tasklist__stat" key={s.key}>
            <i style={{ background: s.color }} />
            {s.label}
            <b className="num">{counts[s.key]}</b>
          </span>
        ))}
      </div>

      <div className="star-tasklist__body">
        {nodes.length === 0 ? (
          <div className="star-tasklist__empty">
            <div className="star-tasklist__empty-text">当前筛选下没有任务</div>
            {filterActive && onClearFilter && (
              <button
                type="button"
                className="star-tasklist__empty-clear"
                onClick={onClearFilter}
              >
                清除筛选
              </button>
            )}
          </div>
        ) : (
          nodes.map((node) => {
            const overdue = node.task?.overdueDays ?? 0;
            const progress = node.task?.progressPct ?? 0;
            return (
              <button
                type="button"
                key={node.id}
                className={['star-tasklist__row', node.id === selectedId ? 'on' : ''].join(' ')}
                onClick={() => onSelect(node.id)}
              >
                <span className="tn">
                  <span className="d" style={{ background: node.colorHex }} />
                  {node.name}
                </span>
                <span className="tm">
                  <span className="tag">{node.formLabel}</span>
                  <span>{node.task?.ownerName ?? '未指派'}</span>
                  <span className="pg">{progress.toFixed(1)}%</span>
                  {overdue > 0 && <span className="overdue">逾期 {overdue} 天</span>}
                </span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
